<template>
	<div v-if="!appStore.isMobile" class="data-table__header pb-0">
		<DataTableCell class="mr-150" :is-title="true" :is-header="true"></DataTableCell>
		<DataTableCell
			v-for="(column, index) in headerRow.fields"
			:key="index"
			:is-header="true"
			class="reit-historical-performance"
			:class="{ 'reit-historical-performance-quarter-end': (index + 1) % 3 === 0 }"
		>
			{{ column.content }}
		</DataTableCell>
	</div>
	<nav v-else class="nav-horizontal eta zeta-md mb-50">
		<ul class="display-flex justify-content-center">
			<li
				v-for="(column, index) in headerRow.fields"
				:key="index"
				:class="{ active: mobileColumn === column.columnName }"
				@click="updateMobileColumn(column.columnName)"
			>
				<BaseLinkButton>{{ column.content }}</BaseLinkButton>
			</li>
		</ul>
		<div class="data-table__header mt-50 pb-0">
			<DataTableCell class="reit-historical-performance" style="border-top: 0" />
			<DataTableCell
				v-for="(column, index) in getMonthsForQuarter(mobileColumn)"
				:key="index"
				class="reit-historical-performance"
				style="border-top: 0"
			>
				<slot :name="`row-title-${camelLettersToKebab(column.columnName)}`">
					{{ column.content }}
				</slot>
			</DataTableCell>
		</div>
	</nav>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { DataTableItem, DataTableItemRow } from 'types/data-table';
import { camelLettersToKebab } from '@filters/shared-filters';
import DataTableCell from '@components/account/data-table-cell.vue';
import { useAppStore } from '@stores/app';

interface Emits {
	(e: 'updateMobileColumn', value: string): void;
}
const emit = defineEmits<Emits>();

const appStore = useAppStore();

const defaultMobileColumn = ref('ytd');

const mobileColumn = ref('');

const desktopHeaderRow: DataTableItemRow = {
	id: 'historicalReturnsTable',
	rowName: '',
	fields: [
		{ columnName: 'jan', content: 'Jan' },
		{ columnName: 'feb', content: 'Feb' },
		{ columnName: 'mar', content: 'Mar' },
		{ columnName: 'apr', content: 'Apr' },
		{ columnName: 'may', content: 'May' },
		{ columnName: 'jun', content: 'Jun' },
		{ columnName: 'jul', content: 'Jul' },
		{ columnName: 'aug', content: 'Aug' },
		{ columnName: 'sep', content: 'Sep' },
		{ columnName: 'oct', content: 'Oct' },
		{ columnName: 'nov', content: 'Nov' },
		{ columnName: 'dec', content: 'Dec' },
		{ columnName: 'ytd', content: 'YTD' }
	]
};

const mobileHeaderRow: DataTableItemRow = {
	id: 'mobileHistoricalReturnsTable',
	rowName: '',
	fields: [
		{ columnName: 'q1', content: 'Q1' },
		{ columnName: 'q2', content: 'Q2' },
		{ columnName: 'q3', content: 'Q3' },
		{ columnName: 'q4', content: 'Q4' },
		{ columnName: 'ytd', content: 'YTD' }
	]
};

const headerRow = computed((): DataTableItemRow => {
	return appStore.isMobile ? mobileHeaderRow : desktopHeaderRow;
});

function getMonthsForQuarter(quarter: string): Array<DataTableItem> {
	switch (quarter) {
		case 'q1':
			return desktopHeaderRow.fields.slice(0, 3);
		case 'q2':
			return desktopHeaderRow.fields.slice(3, 6);
		case 'q3':
			return desktopHeaderRow.fields.slice(6, 9);
		case 'q4':
			return desktopHeaderRow.fields.slice(9, 12);
		case 'ytd':
			return desktopHeaderRow.fields.slice(12, 13);
		default:
			return desktopHeaderRow.fields;
	}
}

function updateMobileColumn(mobileColumnParam: string): void {
	mobileColumn.value = mobileColumnParam;
	emit('updateMobileColumn', mobileColumnParam);
}

function setup(): void {
	mobileColumn.value = defaultMobileColumn.value ?? headerRow.value.fields[0].columnName;
}
setup();
</script>
